import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import SurveyMap from 'components/Map/SurveyMap';
import { useIsMobile } from 'utils/viewport';
import JobsPanel from './JobsPanel';
import SitesPanel from './SitesPanel';
import SidebarTabs from './SidebarTabs';
import CreateJobModal from './CreateJobModal';
import SiteModal from './SiteModal';
import CreateGroupModal from './CreateGroupModal';
import SelectionFooter from './SelectionFooter';
import { MAP_DEFAULT_CENTER } from 'constants/map.constant';
import {
  SITES_FETCH,
  SITE_CREATE,
  SITE_UPDATE,
  JOBS_FETCH,
  JOB_CREATE,
  JOB_UPDATE,
  JOB_SELECT,
  TREES_FETCH,
  TREE_CREATE,
  TREE_UPDATE,
  TREES_ASSIGN_JOB,
  GROUPS_FETCH,
  GROUP_CREATE,
  GROUP_ADD_TREES,
  GROUP_REMOVE_TREES,
  GROUP_DELETE,
  LOOKUP_ENUMS_FETCH,
  LOOKUP_SPECIES_FETCH,
  MAP_SELECT_TREE,
  MAP_TOGGLE_TREE_SELECTION,
  MAP_CLEAR_SELECTION,
  MAP_TOGGLE_SIDEBAR,
  MAP_SET_SIDEBAR,
  MAP_SET_SIDEBAR_TAB,
  MAP_SET_PLACE_MODE,
  MAP_SET_SITE_PICK_MODE,
  MAP_SET_SELECTION_MODE,
  MAP_SET_LAST_TREE_DRAFT,
} from 'redux/actions';

const MapView = () => {
  const dispatch = useDispatch();
  const { list: sites } = useSelector((s) => s.sites);
  const { list: jobs, selected: selectedJob } = useSelector((s) => s.jobs);
  const { list: trees } = useSelector((s) => s.trees);
  const { list: groups } = useSelector((s) => s.groups);
  const { enums, species } = useSelector((s) => s.lookup);
  const {
    selected_tree_id,
    selected_tree_ids,
    sidebar_open,
    place_mode,
    site_pick_mode,
    selection_mode,
    sidebar_tab,
    last_tree_draft,
  } = useSelector((s) => s.map);

  const [showCreateJob, setShowCreateJob] = useState(false);
  const [createJobSiteId, setCreateJobSiteId] = useState(null);
  const [showCreateSite, setShowCreateSite] = useState(false);
  const [editingSite, setEditingSite] = useState(null);
  const [siteFormDraft, setSiteFormDraft] = useState(null);
  const [selectedSiteId, setSelectedSiteId] = useState(null);
  const [showCreateGroup, setShowCreateGroup] = useState(false);
  const [openTreeDetailId, setOpenTreeDetailId] = useState(null);
  const [openGroupId, setOpenGroupId] = useState(null);
  const [pendingPin, setPendingPin] = useState(null);
  const [mapFocusTick, setMapFocusTick] = useState(0);
  const isMobile = useIsMobile();

  const setSidebarOpen = useCallback((open) => {
    dispatch(MAP_SET_SIDEBAR.requestAction({ open }));
  }, [dispatch]);

  const openJobsSidebar = useCallback(() => {
    dispatch(MAP_SET_SIDEBAR_TAB.requestAction({ tab: 'jobs' }));
    setSidebarOpen(true);
  }, [dispatch, setSidebarOpen]);

  useEffect(() => {
    if (!isMobile) return undefined;
    if (sidebar_open) {
      document.body.classList.add('mobile-menu-open');
    } else {
      document.body.classList.remove('mobile-menu-open');
    }
    return () => document.body.classList.remove('mobile-menu-open');
  }, [isMobile, sidebar_open]);

  const jobGroups = useMemo(
    () => (selectedJob ? groups.filter((g) => g.job_id === selectedJob.job_id) : []),
    [groups, selectedJob]
  );

  const selectedInJobIds = useMemo(() => {
    if (!selectedJob) return [];
    return selected_tree_ids.filter((id) =>
      trees.some((t) => t.tree_id === id && t.job_id === selectedJob.job_id)
    );
  }, [selectedJob, selected_tree_ids, trees]);

  const hasActiveSelection = selectedInJobIds.length > 0;
  const showSelectionFooter = Boolean(
    selectedJob
    && !pendingPin
    && (selection_mode || hasActiveSelection)
    && (!isMobile || !sidebar_open)
  );
  const mapSelectionActive = selection_mode || (hasActiveSelection && (!isMobile || !sidebar_open));

  const toggleTreeInJobSelection = useCallback((tree) => {
    if (!selectedJob || tree.job_id !== selectedJob.job_id) return;
    dispatch(MAP_TOGGLE_TREE_SELECTION.requestAction({ tree_id: tree.tree_id }));
  }, [dispatch, selectedJob]);

  useEffect(() => {
    dispatch(SITES_FETCH.requestAction());
    dispatch(JOBS_FETCH.requestAction());
    dispatch(TREES_FETCH.requestAction());
    dispatch(GROUPS_FETCH.requestAction());
    dispatch(LOOKUP_ENUMS_FETCH.requestAction());
    dispatch(LOOKUP_SPECIES_FETCH.requestAction());
  }, [dispatch]);

  useEffect(() => {
    if (selectedJob || selected_tree_ids.length === 0 || trees.length === 0 || jobs.length === 0) return;
    const tree = trees.find((t) => selected_tree_ids.includes(t.tree_id));
    if (!tree?.job_id) return;
    const job = jobs.find((j) => j.job_id === tree.job_id);
    if (job) {
      dispatch(JOB_SELECT.requestAction(job));
      dispatch(MAP_SET_SIDEBAR_TAB.requestAction({ tab: 'jobs' }));
    }
  }, [selectedJob, selected_tree_ids, trees, jobs, dispatch]);

  const mapCenter = useMemo(() => {
    const site = (selectedSiteId && sites.find((s) => s.site_id === selectedSiteId))
      || sites.find((s) => s.gps_lat != null)
      || sites[0];
    if (site?.gps_lat != null && site?.gps_lng != null) {
      return { lat: site.gps_lat, lng: site.gps_lng };
    }
    return MAP_DEFAULT_CENTER;
  }, [sites, selectedSiteId]);

  const focusTreeOnMap = useCallback((tree) => {
    if (!tree) return;
    dispatch(MAP_SELECT_TREE.requestAction({ tree_id: tree.tree_id }));
    setMapFocusTick((n) => n + 1);
  }, [dispatch]);

  const openTreeInJob = useCallback((tree, openDetail = true) => {
    if (!tree) return;
    focusTreeOnMap(tree);
    if (tree.job_id) {
      const job = jobs.find((j) => j.job_id === tree.job_id);
      if (job) dispatch(JOB_SELECT.requestAction(job));
    }
    if (openDetail) setOpenTreeDetailId(tree.tree_id);
  }, [dispatch, jobs, focusTreeOnMap]);

  const handleTreeClick = useCallback((tree) => {
    if (mapSelectionActive) {
      toggleTreeInJobSelection(tree);
      return;
    }
    setPendingPin(null);
    setOpenTreeDetailId(null);
    setOpenGroupId(null);
    openTreeInJob(tree, false);
    if (isMobile) {
      openJobsSidebar();
    }
  }, [mapSelectionActive, toggleTreeInJobSelection, openTreeInJob, isMobile, openJobsSidebar]);

  const handleTreeShiftClick = useCallback((tree) => {
    toggleTreeInJobSelection(tree);
    if (!selection_mode) {
      dispatch(MAP_SET_SELECTION_MODE.requestAction({ enabled: true }));
      setOpenTreeDetailId(null);
      setOpenGroupId(null);
      if (isMobile) {
        setSidebarOpen(false);
      }
    }
  }, [dispatch, selection_mode, toggleTreeInJobSelection, isMobile, setSidebarOpen]);

  const handleToggleTreeSelection = useCallback((tree) => {
    toggleTreeInJobSelection(tree);
  }, [toggleTreeInJobSelection]);

  const handleToggleSelectionMode = useCallback(() => {
    if (selection_mode) {
      dispatch(MAP_SET_SELECTION_MODE.requestAction({ enabled: false }));
    } else {
      setPendingPin(null);
      setOpenTreeDetailId(null);
      setOpenGroupId(null);
      dispatch(MAP_SET_PLACE_MODE.requestAction({ enabled: false }));
      dispatch(MAP_SET_SELECTION_MODE.requestAction({ enabled: true }));
      if (isMobile) {
        setSidebarOpen(false);
      } else if (!sidebar_open) {
        setSidebarOpen(true);
      }
    }
  }, [dispatch, selection_mode, sidebar_open, isMobile, setSidebarOpen]);

  const clearTreeSelection = useCallback(() => {
    setShowCreateGroup(false);
    dispatch(MAP_CLEAR_SELECTION.requestAction());
  }, [dispatch]);

  const handleDoneSelection = useCallback(() => {
    dispatch(MAP_SET_SELECTION_MODE.requestAction({ enabled: false }));
    setSidebarOpen(true);
  }, [dispatch, setSidebarOpen]);

  const handleClearSelection = useCallback(() => {
    clearTreeSelection();
  }, [clearTreeSelection]);

  const dropPinAt = useCallback((coords) => {
    if (selection_mode || site_pick_mode) return;
    setPendingPin(coords);
    setOpenTreeDetailId(null);
    if (!sidebar_open) {
      setSidebarOpen(true);
    }
  }, [selection_mode, site_pick_mode, sidebar_open, setSidebarOpen]);

  const handleLongPress = useCallback((coords) => {
    dropPinAt(coords);
  }, [dropPinAt]);

  const handleMapClick = useCallback((coords) => {
    if (site_pick_mode) {
      setSiteFormDraft((prev) => ({
        ...prev,
        gps_lat: coords.lat,
        gps_lng: coords.lng,
      }));
      dispatch(MAP_SET_SITE_PICK_MODE.requestAction({ enabled: false }));
      return;
    }
    dropPinAt(coords);
  }, [dispatch, dropPinAt, site_pick_mode]);

  const handleTogglePlaceMode = useCallback(() => {
    if (place_mode) {
      dispatch(MAP_SET_PLACE_MODE.requestAction({ enabled: false }));
      setPendingPin(null);
    } else {
      dispatch(MAP_SET_PLACE_MODE.requestAction({ enabled: true }));
      setSidebarOpen(false);
    }
  }, [dispatch, place_mode, setSidebarOpen]);

  const handleIgnoreNewTree = useCallback(() => {
    setPendingPin(null);
    dispatch(MAP_SET_PLACE_MODE.requestAction({ enabled: false }));
  }, [dispatch]);

  const handleSaveNewTree = useCallback((data) => {
    if (!selectedJob) return;
    dispatch(TREE_CREATE.requestAction({
      ...data,
      successCb: (tree) => {
        dispatch(MAP_SET_LAST_TREE_DRAFT.requestAction({
          genus: tree.genus,
          species: tree.species,
          common_name: tree.common_name,
          form: tree.form,
          number_of_stems: tree.number_of_stems,
        }));
        setPendingPin(null);
        dispatch(MAP_SET_PLACE_MODE.requestAction({ enabled: false }));
        dispatch(TREES_FETCH.requestAction({
          successCb: () => {
            focusTreeOnMap(tree);
            setOpenTreeDetailId(null);
          },
        }));
      },
    }));
  }, [dispatch, selectedJob, focusTreeOnMap]);

  const closeSiteModal = useCallback(() => {
    setShowCreateSite(false);
    setEditingSite(null);
    setSiteFormDraft(null);
    dispatch(MAP_SET_SITE_PICK_MODE.requestAction({ enabled: false }));
  }, [dispatch]);

  const handleCreateSite = useCallback((data) => {
    dispatch(SITE_CREATE.requestAction({
      ...data,
      successCb: (site) => {
        closeSiteModal();
        setSelectedSiteId(site.site_id);
        dispatch(MAP_SET_SIDEBAR_TAB.requestAction({ tab: 'sites' }));
      },
    }));
  }, [dispatch, closeSiteModal]);

  const handleUpdateSite = useCallback((data) => {
    if (!editingSite) return;
    dispatch(SITE_UPDATE.requestAction({
      ...data,
      site_id: editingSite.site_id,
      successCb: (site) => {
        closeSiteModal();
        setSelectedSiteId(site.site_id);
      },
    }));
  }, [dispatch, editingSite, closeSiteModal]);

  const handlePickSiteOnMap = useCallback((form) => {
    setSiteFormDraft(form);
    dispatch(MAP_SET_SITE_PICK_MODE.requestAction({ enabled: true }));
    setSidebarOpen(false);
  }, [dispatch, setSidebarOpen]);

  const prevPlaceMode = useRef(place_mode);
  useEffect(() => {
    if (prevPlaceMode.current && !place_mode) {
      setPendingPin(null);
    }
    prevPlaceMode.current = place_mode;
  }, [place_mode]);

  const handleSelectSite = useCallback((site) => {
    setSelectedSiteId(site.site_id);
    if (!sidebar_open) {
      setSidebarOpen(true);
    }
  }, [sidebar_open, setSidebarOpen]);

  const handleCreateJobForSite = useCallback((site) => {
    setCreateJobSiteId(site.site_id);
    setShowCreateJob(true);
  }, []);

  const handleOpenJobFromSite = useCallback((job) => {
    dispatch(JOB_SELECT.requestAction(job));
    dispatch(MAP_SET_SIDEBAR_TAB.requestAction({ tab: 'jobs' }));
    setOpenTreeDetailId(null);
    setOpenGroupId(null);
    setPendingPin(null);
    setSidebarOpen(true);
  }, [dispatch, setSidebarOpen]);

  const handleSidebarTab = useCallback((tab) => {
    dispatch(MAP_SET_SIDEBAR_TAB.requestAction({ tab }));
  }, [dispatch]);

  const handleCreateJob = useCallback((data) => {
    dispatch(JOB_CREATE.requestAction({
      ...data,
      successCb: (job) => {
        setShowCreateJob(false);
        setCreateJobSiteId(null);
        dispatch(JOB_SELECT.requestAction(job));
        dispatch(MAP_SET_SIDEBAR_TAB.requestAction({ tab: 'jobs' }));
        setSidebarOpen(true);
      },
    }));
  }, [dispatch, setSidebarOpen]);

  const handleSaveJob = useCallback((data, onDone) => {
    dispatch(JOB_UPDATE.requestAction({
      ...data,
      successCb: () => onDone?.(),
    }));
  }, [dispatch]);

  const handleSaveTree = useCallback((data, onDone) => {
    dispatch(TREE_UPDATE.requestAction({
      ...data,
      successCb: () => {
        dispatch(TREES_FETCH.requestAction());
        onDone?.();
      },
    }));
  }, [dispatch]);

  const handleAssignToJob = useCallback(() => {
    if (!selectedJob || selected_tree_ids.length === 0) return;
    dispatch(TREES_ASSIGN_JOB.requestAction({
      tree_ids: selected_tree_ids,
      job_id: selectedJob.job_id,
      successCb: () => {
        clearTreeSelection();
        dispatch(TREES_FETCH.requestAction());
      },
    }));
  }, [dispatch, selectedJob, selected_tree_ids, clearTreeSelection]);

  const handleCreateGroup = useCallback(({ name }) => {
    if (!selectedJob || selectedInJobIds.length === 0) return;
    dispatch(GROUP_CREATE.requestAction({
      job_id: selectedJob.job_id,
      name,
      tree_ids: selectedInJobIds,
      successCb: () => {
        clearTreeSelection();
        dispatch(GROUPS_FETCH.requestAction());
        dispatch(TREES_FETCH.requestAction());
      },
    }));
  }, [dispatch, selectedJob, selectedInJobIds, clearTreeSelection]);

  const handleAddToGroup = useCallback((groupId) => {
    if (!groupId || selectedInJobIds.length === 0) return;
    dispatch(GROUP_ADD_TREES.requestAction({
      group_id: groupId,
      tree_ids: selectedInJobIds,
      successCb: () => {
        clearTreeSelection();
        dispatch(GROUPS_FETCH.requestAction());
        dispatch(TREES_FETCH.requestAction());
      },
    }));
  }, [dispatch, selectedInJobIds, clearTreeSelection]);

  const refreshGroupsAndTrees = useCallback(() => {
    dispatch(GROUPS_FETCH.requestAction());
    dispatch(TREES_FETCH.requestAction());
  }, [dispatch]);

  const handleDeleteGroup = useCallback((group) => {
    if (!group) return;
    const label = group.name || 'this group';
    if (!window.confirm(`Delete "${label}"? Trees will stay on the job but won't be grouped.`)) return;
    dispatch(GROUP_DELETE.requestAction({
      group_id: group.group_id,
      successCb: () => {
        if (openGroupId === group.group_id) setOpenGroupId(null);
        refreshGroupsAndTrees();
      },
    }));
  }, [dispatch, openGroupId, refreshGroupsAndTrees]);

  const handleRemoveTreeFromGroup = useCallback((group, tree) => {
    if (!group || !tree) return;
    const name = tree.common_name || `${tree.genus} ${tree.species}`;
    if (!window.confirm(`Remove "${name}" from this group?`)) return;
    dispatch(GROUP_REMOVE_TREES.requestAction({
      group_id: group.group_id,
      tree_ids: [tree.tree_id],
      successCb: (result) => {
        if (result?.deleted && openGroupId === group.group_id) setOpenGroupId(null);
        refreshGroupsAndTrees();
      },
    }));
  }, [dispatch, openGroupId, refreshGroupsAndTrees]);

  const handleSelectJob = useCallback((job) => {
    dispatch(JOB_SELECT.requestAction(job));
    dispatch(MAP_SELECT_TREE.requestAction({ tree_id: null }));
    setOpenTreeDetailId(null);
    setOpenGroupId(null);
    setPendingPin(null);
  }, [dispatch]);

  const handleBackToJobsList = useCallback(() => {
    dispatch(JOB_SELECT.requestAction(null));
    dispatch(MAP_SELECT_TREE.requestAction({ tree_id: null }));
    setOpenTreeDetailId(null);
    setOpenGroupId(null);
    setPendingPin(null);
  }, [dispatch]);

  const handleSelectGroup = useCallback((group) => {
    setOpenGroupId(group.group_id);
    setOpenTreeDetailId(null);
    dispatch(MAP_SELECT_TREE.requestAction({ tree_id: null }));
    if (isMobile) {
      setSidebarOpen(false);
    } else if (!sidebar_open) {
      setSidebarOpen(true);
    }
  }, [dispatch, isMobile, sidebar_open, setSidebarOpen]);

  const handleCloseGroupDetail = useCallback(() => {
    setOpenGroupId(null);
  }, []);

  const handleFocusTree = useCallback((tree) => {
    if (mapSelectionActive) {
      toggleTreeInJobSelection(tree);
      return;
    }
    focusTreeOnMap(tree);
    setOpenTreeDetailId(null);
    setOpenGroupId(null);
    if (isMobile) {
      setSidebarOpen(false);
    }
  }, [mapSelectionActive, toggleTreeInJobSelection, focusTreeOnMap, isMobile, setSidebarOpen]);

  const handleEditTree = useCallback((tree) => {
    focusTreeOnMap(tree);
    setOpenTreeDetailId(tree.tree_id);
    if (isMobile) {
      openJobsSidebar();
    }
  }, [focusTreeOnMap, isMobile, openJobsSidebar]);

  const handleCloseTreeDetail = useCallback(() => {
    setOpenTreeDetailId(null);
  }, []);

  const layoutClass = ['map-layout', showSelectionFooter ? 'selecting' : ''].filter(Boolean).join(' ');

  return (
    <div className={layoutClass}>
      {isMobile && sidebar_open && (
        <button
          type="button"
          className="sidebar-backdrop"
          aria-label="Close menu"
          onClick={() => setSidebarOpen(false)}
        />
      )}
      <div className={`sidebar ${sidebar_open ? '' : 'collapsed'}`}>
        <SidebarTabs activeTab={sidebar_tab} onChange={handleSidebarTab} />
        <div className="sidebar-content">
          {sidebar_tab === 'sites' ? (
            <SitesPanel
              sites={sites}
              jobs={jobs}
              trees={trees}
              selectedSiteId={selectedSiteId}
              onSelectSite={handleSelectSite}
              onCreateSite={() => {
                setEditingSite(null);
                setSiteFormDraft(null);
                setShowCreateSite(true);
              }}
              onEditSite={(site) => {
                setShowCreateSite(false);
                setSiteFormDraft(null);
                setEditingSite(site);
              }}
              onCreateJobForSite={handleCreateJobForSite}
              onOpenJob={handleOpenJobFromSite}
            />
          ) : (
            <JobsPanel
              jobs={jobs}
              sites={sites}
              selectedJob={selectedJob}
              trees={trees}
              groups={groups}
              enums={enums}
              species={species}
              lastTreeDraft={last_tree_draft}
              selectedTreeId={selected_tree_id}
              selectedTreeIds={selected_tree_ids}
              selectionMode={selection_mode}
              openTreeDetailId={openTreeDetailId}
              openGroupId={openGroupId}
              pendingPin={pendingPin}
              onSelectJob={handleSelectJob}
              onBack={handleBackToJobsList}
              onCreateJob={() => {
                if (sites.length === 0) {
                  dispatch(MAP_SET_SIDEBAR_TAB.requestAction({ tab: 'sites' }));
                  setShowCreateSite(true);
                  return;
                }
                setCreateJobSiteId(null);
                setShowCreateJob(true);
              }}
              onFocusTree={handleFocusTree}
              onTreeShiftClick={handleTreeShiftClick}
              onToggleTreeSelection={handleToggleTreeSelection}
              onToggleSelectionMode={handleToggleSelectionMode}
              onTogglePlaceMode={handleTogglePlaceMode}
              placeMode={place_mode}
              onSelectGroup={handleSelectGroup}
              onEditTree={handleEditTree}
              onSaveJob={handleSaveJob}
              onSaveTree={handleSaveTree}
              onSaveNewTree={handleSaveNewTree}
              onIgnoreNewTree={handleIgnoreNewTree}
              onCloseTreeDetail={handleCloseTreeDetail}
              onCloseGroupDetail={handleCloseGroupDetail}
              onDeleteGroup={handleDeleteGroup}
              onRemoveTreeFromGroup={handleRemoveTreeFromGroup}
            />
          )}
        </div>
      </div>

      <div className="map-container">
        {showSelectionFooter && (
          <SelectionFooter
            count={selectedInJobIds.length}
            canCreateGroup={selectedInJobIds.length >= 2}
            jobGroups={jobGroups}
            onCreateGroup={() => setShowCreateGroup(true)}
            onAddToGroup={handleAddToGroup}
            onClear={handleClearSelection}
            onConfirm={handleDoneSelection}
          />
        )}
        <SurveyMap
          trees={trees}
          groups={groups}
          selectedTreeId={selected_tree_id}
          selectedTreeIds={selected_tree_ids}
          focusTreeId={selected_tree_id}
          focusGroupId={openGroupId}
          highlightGroupId={openGroupId}
          focusSiteId={selectedSiteId}
          sites={sites}
          pendingPin={pendingPin}
          placeMode={place_mode && !site_pick_mode}
          sitePickMode={site_pick_mode}
          selectionMode={mapSelectionActive}
          onLongPress={site_pick_mode || selection_mode ? undefined : handleLongPress}
          onMapClick={handleMapClick}
          onSitePick={handleMapClick}
          onTreeClick={handleTreeClick}
          onTreeShiftClick={handleTreeShiftClick}
          center={mapCenter}
          sidebarOpen={sidebar_open}
          focusTick={mapFocusTick}
          isMobile={isMobile}
        />
      </div>

      {(showCreateSite || editingSite) && !site_pick_mode && (
        <SiteModal
          site={editingSite}
          draft={siteFormDraft}
          enums={enums}
          onClose={closeSiteModal}
          onSubmit={editingSite ? handleUpdateSite : handleCreateSite}
          onPickOnMap={handlePickSiteOnMap}
        />
      )}

      {showCreateJob && (
        <CreateJobModal
          sites={sites}
          defaultSiteId={createJobSiteId}
          onClose={() => {
            setShowCreateJob(false);
            setCreateJobSiteId(null);
          }}
          onSubmit={handleCreateJob}
        />
      )}

      {showCreateGroup && (
        <CreateGroupModal
          treeCount={selectedInJobIds.length}
          jobName={selectedJob?.name}
          onClose={() => setShowCreateGroup(false)}
          onSubmit={handleCreateGroup}
        />
      )}
    </div>
  );
};

export default MapView;
